/**
 * ControlsToolbar — second toolbar row rendered inside FinishTimeBody.
 *
 * Contains: About to Finish label, count badge, +,
 *           On Water, SN Alpha, Real-time/Manual toggle, Process Results.
 *           (The live clock moved up to the page heading / fullscreen toolbar.)
 *
 * React.memo prevents re-render when only grid data changes (handlers are stable refs).
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CheckCircleRounded } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { MAX_WATCHLIST_SLOTS } from "./constants";
import { ToolbarSwitch } from "./StyledComponents";

// Self-contained count number updated imperatively via ref — the About board
// reports its count on the FIRST keystroke in a box (slot taken), and routing
// that through FinishTimeBody state re-rendered BOTH grids mid-keystroke (the
// "first character sticks" lag on 200+ sessions). This way only this tiny
// component re-renders. `storeRef` holds the last reported count so a REMOUNT
// (the About block unmounts in manual mode) starts from the real value
// instead of 0/6.
export const AboutCountValue = React.forwardRef(({ storeRef }, ref) => {
  const [count, setCount] = useState(() => storeRef?.current ?? 0);
  React.useImperativeHandle(ref, () => ({ setCount }), []);
  return <>{count}</>;
});

// The "+" add-box button — disabled while the board is full (6/6). Driven by
// the same imperative count store (state here would re-render the grids per
// board keystroke; see AboutCountValue).
export const AboutAddButton = React.forwardRef(
  ({ storeRef, onClick, compactTablet }, ref) => {
    const [disabled, setDisabled] = useState(
      () => (storeRef?.current ?? 0) >= MAX_WATCHLIST_SLOTS,
    );
    React.useImperativeHandle(ref, () => ({ setDisabled }), []);
    return (
      <Box
        onClick={disabled ? undefined : onClick}
        title={disabled ? "About to Finish is full" : "Add box"}
        sx={{
          width: 22,
          height: 22,
          borderRadius: 1,
          border: "1px solid #f0a500",
          color: "#8a6d00",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.35 : 1,
          fontSize: compactTablet ? 14 : 16,
          fontWeight: 700,
          lineHeight: 1,
          userSelect: "none",
          "&:hover": disabled ? {} : { bgcolor: "#fff3cd" },
        }}
      >
        <AddIcon sx={{ fontSize: compactTablet ? 16 : 18 }} />
      </Box>
    );
  },
);

const ControlsToolbar = React.memo(
  ({
    aboutCountRef,
    aboutCountStoreRef,
    aboutAddRef,
    onAddBlankBox,
    onWater,
    setOnWater,
    snWithAlpha,
    setSnWithAlpha,
    currentMode,
    setMode,
    isPlace,
    processFlag,
    noEntries,
    onProcessResults,
  }) => {
    const compactTablet = useMediaQuery("(max-width:1024px)");
    const CTRL_H = compactTablet ? 32 : 36;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: compactTablet ? 0.75 : 1,
        }}
      >
        {/* Left: About to Finish label + count + + (real-time only) */}
        {currentMode === "real-time" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "var(--ty-primary-color, #356dad)",
                fontSize: compactTablet ? 12 : 13,
                textTransform: "uppercase",
                letterSpacing: 1.5,
              }}
            >
              About to Finish
            </Typography>
            <Box
              component="span"
              sx={{
                bgcolor: "#fff3cd",
                color: "#8a6d00",
                border: "1px solid #f0a500",
                borderRadius: 1,
                px: 0.75,
                py: "1px",
                fontSize: compactTablet ? 12 : 11,
                fontWeight: 700,
              }}
            >
              <AboutCountValue ref={aboutCountRef} storeRef={aboutCountStoreRef} />/{MAX_WATCHLIST_SLOTS}
            </Box>
            <AboutAddButton
              ref={aboutAddRef}
              storeRef={aboutCountStoreRef}
              onClick={onAddBlankBox}
              compactTablet={compactTablet}
            />
          </Box>
        )}

        {/* Right: toggles + Process Results (ml:auto keeps it pinned right in both modes) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: compactTablet ? 0.5 : 1.5,
            flexWrap: "wrap",
            ml: "auto",
          }}
        >
          <ToolbarSwitch
            label="On Water"
            checked={onWater}
            onChange={setOnWater}
            tooltip="Turn on this switch to filter only those boats that have been marked 'on water' on TES."
          />
          <ToolbarSwitch
            label="SN Alpha"
            checked={snWithAlpha}
            onChange={setSnWithAlpha}
          />

          <ToggleButtonGroup
              value={currentMode}
              exclusive
              onChange={(e, val) => {
                if (val) setMode(val);
              }}
              size="small"
              sx={{
                bgcolor: "#eef3f8",
                borderRadius: 999,
                p: "3px",
                height: CTRL_H,
                boxSizing: "border-box",
                "& .MuiToggleButton-root": {
                  border: 0,
                  borderRadius: 999,
                  px: compactTablet ? 1.25 : 2,
                  py: 0.5,
                  py: 0,
                  height: "100%",
                  fontSize: compactTablet ? 10 : 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "var(--ty-secondary-color, #42566c)",
                  "&.Mui-selected": {
                    bgcolor: "var(--ty-primary-color, #356dad)",
                    color: "#fff",
                    "&:hover": {
                      bgcolor: "var(--ty-primary-hovercolor, #2b598d)",
                    },
                  },
                },
              }}
            >
              {/* Manual first — it is the default tab. */}
              <ToggleButton value="manual">Manual</ToggleButton>
              <ToggleButton value="real-time">Real-time</ToggleButton>
          </ToggleButtonGroup>

          {/* Always the full "Process Results" label — an icon-only button read
              as an unlabeled tick. The tick is dropped at the same width where
              the header actions collapse to icons (compactTablet), so the label
              alone carries the button and the controls row stays on one line.
              The `&.ty_secondary_btn` override kills the global
              `@media (max-width:900.98px) { width: 100% }` in buttons.scss —
              without it this button stretched to the toolbar width and was
              forced onto its own line on tablet/mobile. */}
          <Button
            className="ty_secondary_btn"
            size="small"
            startIcon={compactTablet ? null : <CheckCircleRounded />}
            onClick={onProcessResults}
            disabled={processFlag || noEntries}
            sx={{
              color: "var(--ty-primary-color, #356dad)",
              fontWeight: 700,
              border: "1px solid #dceaf0",
              whiteSpace: "nowrap",
              fontSize: compactTablet ? 10 : undefined,
              px: compactTablet ? 1 : undefined,
              "&.ty_secondary_btn": {
                width: "auto",
                flexShrink: 0,
                height: CTRL_H,
                minHeight: CTRL_H,
                paddingTop: 0,
                paddingBottom: 0,
                lineHeight: 1,
                // If the row still can't fit it on the narrowest phones, it
                // wraps as a compact right-aligned pill, never full width.
                ml: "auto",
              },
            }}
          >
            <span style={{ position: "relative", top: "-1px" }}>
              Process Results
            </span>
          </Button>
        </Box>
      </Box>
    );
  },
);

export default ControlsToolbar;
